import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, catchError, forkJoin, iif, map, Observable, of, switchMap } from 'rxjs';

import {
    IContractBeneficiario, IContractInput, IContractInputsForm
} from 'src/app/models/contract';
import {
    FieldsToIgnore, IBeneficiary, IBeneficiaryFormGroup, IGenericContract,
    IGenericContractFormGroup, IGenericResult
} from 'src/app/models/generic';
import { SnackbarType } from 'src/app/models/snackbar-type.enum';
import { IListContract, IListLot } from 'src/app/models/verification';
import { Inputs } from 'src/app/shared/utils/base';
import { environment } from 'src/environments/environment';

import { IContractArrayInput } from '../../models/contract';
import {
    BeneficiaryTypeEnum, IBatchDetails, IBatchDetailsFormGroup, IContractCurrentSituation,
    IContractHistory, IContractMovement
} from '../../models/generic';
import { SHOW_LOADER } from '../loader-interceptor.service';
import { SnackbarService } from '../snackbar.service';
import { ApiBeneficiaryService } from './api-beneficiary.service';
import { ApiVerificationService } from './api-verification.service';

@Injectable({
  providedIn: 'root'
})
export class ApiContractService {
  private _form!: FormGroup<IGenericContractFormGroup>;

  private _isFormReady = new BehaviorSubject<boolean>(false);

  private _checkedContracts = new BehaviorSubject<IListContract[]>([]);

  private _checkedContractsTransfers = new BehaviorSubject<IListLot[]>([]);

  contractInfo!: IGenericContract;
  contractInvoiceInfo!: IGenericContract;
  contractTransferInfo!: IGenericContract;
  beneficiariesData: IBeneficiary[] = [];
  batchDetailsInfo!: IBatchDetails;
  customFields!: { [key: string]: string | number };
  maxFileSize: number = 5120; // 5mb

  FieldsToIgnore = FieldsToIgnore;

  context = new HttpContext().set(SHOW_LOADER, false);

  constructor(
    private _http: HttpClient,
    private _verificationService: ApiVerificationService,
    private _beneficiaryService: ApiBeneficiaryService,
    private _snackbarService: SnackbarService
  ) { }

  /*  baseUrl = `${environment.apis.contracts.baseUrl}/${environment.apis.contracts.microservices.contract}`;*/
  baseUrl = `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.contract}`;
  baseInvoiceUrl = `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.invoiceContract}`;
  baseUrlInvoicesLots = `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.invoiceBatch}`;

  reverseContracts(idContractArray: string[], reversalReasonId: string) {
    return this._http.post(`${this.baseUrl}/reverse-contracts`, { contractIds: idContractArray, reversalReasonId });
  }

  reverseTransfer(idContractArray: string[]) {
    return this._http.post(`${this.baseUrl}/reverse-transfer`, idContractArray);
  }

  updateContract(id: string, contract: any): Observable<IGenericResult<any>> {
    return this._http.put<IGenericResult<any>>(
      `${this.baseUrl}/${id}`,
      contract
    );
  }

  getForm() {
    return this._form;
  }

  get form() {
    return this._form;
  }

  get contractData() {
    return this.contractInfo;
  }

  get batchDetailsNumber() {
    return this.batchDetailsInfo?.BatchNumber ?? '';
  }

  getContractMovements(contractId: string): Observable<IGenericResult<IContractMovement[]>> {
    return this._http.get<IGenericResult<IContractMovement[]>>(
      `${this.baseUrlInvoicesLots}/movements/by-contract/${contractId}`
    );
  }

  getContractHistory(contractId: string): Observable<IGenericResult<IContractHistory>> {
    return this._http.get<IGenericResult<IContractHistory>>(
      `${this.baseUrl}/${contractId}/history`
    );
  }

  getContractCurrentSituation(contractId: string): Observable<IGenericResult<IContractCurrentSituation>> {
    return this._http.get<IGenericResult<IContractCurrentSituation>>(
      `${this.baseUrlInvoicesLots}/current-situation/by-contract/${contractId}`
    );
  }


  getContractsArray(): Observable<IListContract[]> {
    return this._checkedContracts.asObservable();
  }

  getContractsTransferArray(): Observable<IListLot[]> {
    return this._checkedContractsTransfers.asObservable();
  }

  setContractsArray(contract: IListContract) {
    const currentCheckedContracts = this._checkedContracts.value;

    if (!currentCheckedContracts.some((el) => el.ContractId === contract.ContractId)) {
      currentCheckedContracts.push(contract);
    } else {
      const index = currentCheckedContracts.findIndex(
        (el) => el.ContractId === contract.ContractId
      );
      currentCheckedContracts.splice(index, 1);
    }

    this._checkedContracts.next([...currentCheckedContracts]);
  }

  clearContractsSelection(): void {
    this._checkedContracts.next([]);

  }

  checkIfFormIsReady(): Observable<boolean> {
    return this._isFormReady.asObservable();
  }

  updateFormArray(index: number, data: any): void {
    this._form.controls.Beneficiaries.at(index).patchValue(data);
  }

  patchForm(contract: IGenericContract): void {
    if (this.form) {
      this.form.patchValue(contract);
    }
  }

  addNewGroupToFormArray(data: IContractBeneficiario): void {
    const entries = Object.entries(data);

    const controls = {} as { [key: string]: FormControl };

    entries.forEach(([key, value]) => {
      controls[key] = new FormControl(value);
    });

    const group = new FormGroup(controls as unknown as IBeneficiaryFormGroup);

    this._form.controls.Beneficiaries.push(group);
  }

  removeItemFromArray(index: number) {
    this._form.controls.Beneficiaries.removeAt(index);
  }

  initializeForm(
    contractNumber: string,
    queryNumber: string,
    financialInstitution: string,
    getById: boolean = false,
    current: boolean = true
  ): Observable<FormGroup<IGenericContractFormGroup>> {
    let inputsToBuildControls = [
      ...Inputs['detalhes'],
      ...Inputs['financiamento']
    ];

    const contractById$ = this._verificationService.getContractById(
      contractNumber,
      current
    );

    const contractByNumber$ =
      this._verificationService.getContractByNumber(contractNumber, queryNumber, financialInstitution);

    const getContract$ = iif(
      () => getById,
      contractById$,
      contractByNumber$
    ).pipe(
      switchMap((infosFromServer) => {
        this.contractInfo = infosFromServer.Data;

        const getInvoiceBeneficiary$ =
          this._beneficiaryService.getInvoicingBeneficiaries(
            this.contractInfo.Number,
            this.contractInfo.QueryNumber,
            this.contractInfo.FinancialInstitution
          );

        const getTransferBeneficiary$ =
          this._beneficiaryService.getTransferBeneficiaries(
            this.contractInfo.Number,
            this.contractInfo.QueryNumber,
            this.contractInfo.FinancialInstitution
          );

        const getBatchDetails$ = this._verificationService
          .getBatchDetailsByContract(this.contractInfo.ContractId)
          .pipe(
            catchError((err, caught) => {
              this._snackbarService.openSnackBar(
                'Contrato sem lote de faturamento vinculado!',
                SnackbarType.Warn,
                2000
              );
              return of({} as IGenericResult<IBatchDetails>);
            })
          );

        return forkJoin([
          getInvoiceBeneficiary$,
          getTransferBeneficiary$,
          getBatchDetails$
        ]);
      })
    );

    return getContract$.pipe(
      map(([invoiceBeneficiary, transferBeneficiary, batchDetails]) => {
        this.contractInvoiceInfo = invoiceBeneficiary.Data;
        this.contractTransferInfo = transferBeneficiary.Data;
        this.batchDetailsInfo = batchDetails?.Data ?? {};
        this.batchDetailsInfo.InvoiceDate =
          this.batchDetailsInfo?.InvoiceDate?.split('.')[0];

        inputsToBuildControls = this.insertCustomFields(inputsToBuildControls);

        this._form = new FormGroup<IGenericContractFormGroup>({
          ...this.buildFormGroup(inputsToBuildControls as IContractInput[])
            .controls,
          Beneficiaries: this.handleBeneficiaries(),
          InvoiceBeneficiaries: this.buildInvoiceBeneficiaries(
            this.contractInfo.Beneficiaries
          ),
          TransferBeneficiaries: this.buildTransferBeneficiaries(
            this.contractInfo.Beneficiaries
          ),
          BatchDetails: this.buildBatchDetailsFormGroup(this.batchDetailsInfo)
        });

        this._form.patchValue(this.contractInfo);
        this._form.patchValue(this.customFields);
        this._form.patchValue({ Beneficiaries: this.beneficiariesData });

        this._isFormReady.next(true);

        return this._form;
      })
    );
  }

  insertCustomFields(
    inputs: (IContractInput | IContractArrayInput)[]
  ): (IContractInput | IContractArrayInput)[] {
    if (this.contractInfo?.ContractCustomFields?.length > 0) {
      this.customFields = {};

      this.contractInfo.ContractCustomFields.forEach((field, index) => {
        if (field.FieldName.toLowerCase() !== 'vgc') {
          this.customFields[field.FieldName] = field.Value ?? '';

          let contractInput: IContractInput = {
            label: field.FieldName,
            placeholder: field.FieldName,
            controlName: field.FieldName,
            value: field.Value
          };

          inputs.push(contractInput);
        } else {
          this.batchDetailsInfo.VGC = Number(field.Value);
          this.contractInfo.ContractCustomFields.splice(index, 1);
        }
      });
    }

    this.contractInfo['Fee'] = this.contractInvoiceInfo['BeneficiaryFee'] ?? 0;
    this.contractInfo.Beneficiaries.forEach((b) => {
      if (
        b.BeneficiaryType.toLowerCase() === 'subsidiaria' ||
        b.BeneficiaryType.toLowerCase() === 'credimorar'
      ) {
        b.Fee = this.contractInvoiceInfo['BeneficiaryFee'] ?? 0;
      }
    });
    return inputs;
  }

  buildFormGroup(data: IContractInput[]): FormGroup<IGenericContractFormGroup> {
    const controls = {} as IContractInputsForm;

    data.forEach((el) => {
      if (!FieldsToIgnore.includes(el.controlName)) {
        controls[el.controlName] = new FormControl(null);
      }
    });
    return new FormGroup(controls as unknown as IGenericContractFormGroup);
  }

  buildInvoiceBeneficiaries(
    beneficiaries: IBeneficiary[]
  ): FormGroup<IBeneficiaryFormGroup> {
    let invoiceBeneficiary = beneficiaries.find(
      (_bn) => _bn.BeneficiaryType.toLowerCase() === 'parceiro'
    ) as IBeneficiary;

    const beneficiaryGroup: FormGroup<IBeneficiaryFormGroup> = new FormGroup({
      Document: new FormControl<string>(''),
      Name: new FormControl<string>('')
    });

    if (invoiceBeneficiary) {
      beneficiaryGroup.patchValue(invoiceBeneficiary);
    }

    return beneficiaryGroup;
  }

  buildTransferBeneficiaries(
    beneficiaries: IBeneficiary[]
  ): FormGroup<IBeneficiaryFormGroup> {
    let invoiceBeneficiary = beneficiaries.find(
      (_bn) =>
        _bn.BeneficiaryType.toLowerCase() === 'subsidiaria' ||
        _bn.BeneficiaryType.toLowerCase() === 'credimorar'
    ) as IBeneficiary;

    const beneficiaryGroup: FormGroup<IBeneficiaryFormGroup> = new FormGroup({
      Document: new FormControl<string>(''),
      Name: new FormControl<string>('')
    });

    if (invoiceBeneficiary) {
      beneficiaryGroup.patchValue(invoiceBeneficiary);
    }

    return beneficiaryGroup;
  }

  buildBatchDetailsFormGroup(
    data: IBatchDetails
  ): FormGroup<IBatchDetailsFormGroup> {
    const batchDetailsGroup = new FormGroup<IBatchDetailsFormGroup>({
      BatchAmount: new FormControl<number | null>(this.batchDetailsInfo.Amount),
      BatchNumber: new FormControl<string>('', { nonNullable: true }),
      BatchStatus: new FormControl<string>('', { nonNullable: true }),
      ContractAmount: new FormControl<number | null>(null),
      ContractAmountPercent: new FormControl<number | null>(null),
      ContractType: new FormControl<string>('', { nonNullable: true }),
      Nfe: new FormControl<string>('', { nonNullable: true }),
      PaymentDate: new FormControl<string>('', { nonNullable: true }),
      InvoiceDate: new FormControl<string>('', { nonNullable: true }),
      VGC: new FormControl<number | null>(null),
      BatchTransferNumber: new FormControl<string>('', { nonNullable: true })
    });

    batchDetailsGroup.patchValue(data);

    return batchDetailsGroup;
  }

  handleBeneficiaries(): FormArray<FormGroup<IBeneficiaryFormGroup>> {
    const beneficiariesForm = new FormArray<FormGroup<IBeneficiaryFormGroup>>(
      []
    );

    this.beneficiariesData = [this.subsidiary, ...this.beneficiaries];

    this.beneficiariesData.forEach((beneficiary) => {
      const group = {} as {
        [key: string]: FormControl<string | number | null>;
      };

      Object.entries(beneficiary).forEach(([key, value]) => {
        group[key] = new FormControl<string | number | null>(value);
      });

      beneficiariesForm.push(
        new FormGroup<IBeneficiaryFormGroup>(
          group as unknown as IBeneficiaryFormGroup
        )
      );
    });

    return beneficiariesForm;
  }

  get subsidiary(): IBeneficiary {
    const subsidiary = this.contractInfo?.Beneficiaries.find(
      (beneficiary) =>
        beneficiary?.BeneficiaryType?.toLowerCase() ===
        BeneficiaryTypeEnum.Credimorar ||
        beneficiary?.BeneficiaryType?.toLowerCase() ===
        BeneficiaryTypeEnum.Subsidiaria
    )!;

    (subsidiary.Amount = this.contractInvoiceInfo?.['Invoice']?.[
      'Amount'
    ] as number),
      (subsidiary.EstimatedPaymentDate =
        this.contractInvoiceInfo?.EstimatedPaymentDate),
      (subsidiary.PaymentDate = this.contractInvoiceInfo?.PaymentDate);

    return subsidiary;
  }

  get beneficiaries(): IBeneficiary[] {
    if (!this.contractTransferInfo?.Transfers) {
      console.log('A importação do contrato ainda não foi concluída no esquema Transfer.');
      return [];
    }

    return this.contractTransferInfo.Transfers.map((beneficiary) => ({
      BeneficiaryType: beneficiary?.BeneficiaryType,
      BeneficiaryTypeId: this.beneficiaryTypeId(
        beneficiary?.BeneficiaryDocument ?? ''
      ),
      Name: beneficiary?.BeneficiaryName,
      Fee: beneficiary?.BeneficiaryFee,
      Document: beneficiary?.BeneficiaryDocument,
      Amount: beneficiary?.Amount,
      EstimatedPaymentDate: beneficiary?.EstimatedPaymentDate,
      PaymentDate: beneficiary?.PaymentDate
    }));
  }

  beneficiaryTypeId(beneficiaryDocument: string): string {
    return (
      this.contractInfo.Beneficiaries.find(
        (beneficiary) =>
          beneficiary.Document?.replace(/[^\w\s]/gi, '') ===
          beneficiaryDocument?.replace(/[^\w\s]/gi, '')
      )?.BeneficiaryTypeId ?? ''
    );
  }

  approveDisapproveChanges(id: string, approbation: string, reason = '') {
    const body = {} as { Action: string; Comment: string };

    if (approbation === 'reject-changes') {
      (body.Action = ''), (body.Comment = reason);
    }

    return this._http.post<IGenericResult<IGenericContract>>(
      `${this.baseUrl}/${id}/${approbation}`,
      body
    );
  }

  changeStage(id: string, stage: string) {
    const body = {} as { Action: string; Comment: string };

    return this._http.post<IGenericResult<IGenericContract>>(
      `${this.baseUrl}/${id}/change/${stage}`,
      body
    );
  }

  validateFile(target: HTMLInputElement, anyfile: boolean): {
    file: any;
    fileName: string;
  } {
    if (!target.files?.length) {
      return { file: '', fileName: '' };
    }

    const sizeFile = Math.round(target.files[0].size / 1024);

    if (sizeFile > 5120) {
      this._snackbarService.openSnackBar(
        'O arquivo ' + target.files[0].name + ' excede o tamanho de 5MB',
        'danger',
        5000
      );
    }

    if (!anyfile) {
      if (!/.*\/pdf$/.test(target.files[0].type)) {
        this._snackbarService.openSnackBar(
          'Formato incompatível, por favor insira um arquivo PDF',
          'danger',
          5000
        );
        return { file: '', fileName: '' };
      }
    }

    if (sizeFile > this.maxFileSize) {
      this._snackbarService.openSnackBar(
        'Arquivo muito grande, insira um arquivo até 5mb.',
        'danger',
        5000
      );
      return { file: '', fileName: '' };
    }

    const file = target.files[0];
    const fileName = target.files[0].name;

    return { file, fileName };
  }

  getContractChangeCommission(id: string): Observable<any> {
    return this._http.get(`${this.baseUrl}/${id}/contract-commission`);
  }

  getContractChangeCommissionFile(id: string): Observable<any> {
    let url: string = `${this.baseUrl}/${id}/contract-commission-file`;
    return this._http.get(url,
      { responseType: 'blob', context: this.context }
    );
  }

  getReversalReasons(): Observable<any> {
    return this._http.get(`${this.baseInvoiceUrl}/reversal-reasons`);
  }

  updateContractCommission(id: string, contractCommission: any): Observable<any> {
    return this._http.put(`${this.baseUrl}/${id}/update-contract-commission`, contractCommission);
  }

  updateCredimorarContractValue(id: string, updateValue: any): Observable<any> {
    return this._http.post(`${this.baseInvoiceUrl}/${id}/update-credimorar-value`, updateValue);
  }

  createBatchForContract(id: string): Observable<any> {
    return this._http.post(`${this.baseInvoiceUrl}/create-batch-for-contract/${id}`, {});
  }


}
